import { Box, Flex, Heading, Text, useVideo } from '@dapperlabs/core-fe';
import React from 'react';
import { useTranslation } from 'src/lib/translations/useTranslation';
import { PackPriceLabel } from 'src/modules/PackDetailsPage';
import { TimerText } from 'src/modules/PackDetailsPage/components/RowTexts/TimerText';

import { PacksInDropList, PackInSingleDrop } from './components';
import { DistributionExtended } from './types';

interface Props {
  description: string;
  distributions: DistributionExtended[];
  dropDate: string;
  mainHeading: string;
  price: number;
  videoUrl: string;
}

export const UpcomingDrop = ({
  dropDate,
  description,
  mainHeading,
  price,
  videoUrl,
  distributions,
}: Props) => {
  const { t } = useTranslation();
  const { view } = useVideo({
    id: 'upcoming-drop-video',
    pt: 10,
    videoProps: {
      display: 'block',
      h: ['100%', '100%', '100%', '640px'],
      loop: true,
      objectFit: 'cover',
      w: ['100%', '100%', '100%', '730px'],
    },
    videoSrc: videoUrl,
  });

  return (
    <Box>
      <Box
        as='section'
        alignItems='center'
        flexDirection={[
          'column-reverse',
          'column-reverse',
          'column-reverse',
          'row',
        ]}
        gap={{ base: 10, lg: 20 }}
        display='flex'
        mb={14}
      >
        <Box flex={1}>{view}</Box>
        <Box flex={1} mx={{ base: 4, lg: 0 }} p={0}>
          <Box mr={24} border='1px solid #393B45' borderRadius='12px'>
            {dropDate && (
              <Box p={4} borderTopRadius='12px' bgColor='gray.800'>
                <TimerText
                  drawTime={dropDate}
                  timerLabel={t('UpcomingDrops.DropTimerLabel')}
                  timerNumberProps={{
                    fontFamily: 'mono',
                    fontWeight: 400,
                  }}
                  timerUnitProps={{
                    fontFamily: 'mono',
                  }}
                />
              </Box>
            )}
            <Box p={4}>
              <Heading
                as='h1'
                mb={2}
                fontFamily='poster'
                fontSize={['5xl', '6xl']}
                fontWeight='500'
              >
                {mainHeading}
              </Heading>
              <Text
                mb={6}
                color='gray.100'
                fontSize={['md', 'lg']}
                lineHeight='1.4'
              >
                {description}
              </Text>
              {distributions && distributions.length > 1 ? (
                <Flex alignItems='center'>
                  <PackPriceLabel
                    priceTag={distributions[0].price.value}
                    showCurrency={false}
                  />
                  <Text as='span' mx={2} fontSize='3xl'>
                    /
                  </Text>
                  <PackPriceLabel priceTag={distributions[1].price.value} />
                </Flex>
              ) : (
                <PackPriceLabel hasFrom priceTag={price} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box as='section' mt={10} mb={24} mx={{ base: 4, lg: 24 }}>
        {distributions?.length === 1 ? (
          <PackInSingleDrop distribution={distributions[0]} />
        ) : (
          <PacksInDropList distributions={distributions} />
        )}
      </Box>
    </Box>
  );
};
